import { useState } from 'react';
import Modal from './Modal';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import useDialog from '../hooks/useDialog';
import './CalendarManagerModal.scss';

const DEFAULT_COLORS = [
  '#4285f4', // Blue
  '#ea4335', // Red
  '#34a853', // Green
  '#fbbc04', // Yellow
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#ff5722', // Orange
  '#795548', // Brown
];

/**
 * CalendarManagerModal - create, rename, and delete calendars
 */
function CalendarManagerModal({ isOpen, onClose }) {
  const { calendars, currentCalendar, createCalendar, updateCalendar, deleteCalendar, switchCalendar } = useCalendarDataContext();
  const { t } = useTranslation();
  const { confirm } = useDialog();
  
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLORS[0]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allCalendars = calendars?.owned || [];

  const handleStartCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setName('');
    setColor(DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)]);
    setError(null);
  };

  const handleStartEdit = (calendar) => {
    setIsCreating(false);
    setEditingId(calendar.id);
    setName(calendar.name);
    setColor(calendar.color);
    setError(null);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setName('');
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError(t('calendar.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isCreating) {
        const newCalendar = await createCalendar({ name: name.trim(), color });
        switchCalendar(newCalendar.id);
      } else if (editingId) {
        await updateCalendar(editingId, { name: name.trim(), color });
      }
      handleCancel();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (calendar) => {
    if (calendar.isDefault) {
      return;
    }

    const confirmed = await confirm({
      title: t('calendar.deleteCalendar'),
      message: t('calendar.deleteCalendarConfirm', { name: calendar.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await deleteCalendar(calendar.id);
        // If we deleted the current calendar, switch to default
        if (calendar.id === currentCalendar?.id) {
          const defaultCal = allCalendars.find(c => c.isDefault);
          if (defaultCal) {
            switchCalendar(defaultCal.id);
          }
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const renderForm = () => (
    <form className="calendar-manager__form" onSubmit={handleSubmit}>
      <div className="calendar-manager__field">
        <label htmlFor="calendar-name">{t('calendar.name')}</label>
        <input
          id="calendar-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('calendar.namePlaceholder')}
          autoFocus
          disabled={isSubmitting}
        />
      </div>

      <div className="calendar-manager__field">
        <label>{t('calendar.color')}</label>
        <div className="calendar-manager__colors">
          {DEFAULT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`calendar-manager__color-option ${color === c ? 'calendar-manager__color-option--selected' : ''}`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
              disabled={isSubmitting}
            />
          ))}
        </div>
      </div>

      {error && <div className="calendar-manager__error">{error}</div>}

      <div className="calendar-manager__form-actions">
        <button
          type="button"
          className="calendar-manager__btn calendar-manager__btn--secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="calendar-manager__btn calendar-manager__btn--primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('common.saving') : (isCreating ? t('common.create') : t('common.save'))}
        </button>
      </div>
    </form>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('calendar.manageCalendars')}>
      <div className="calendar-manager">
        {(isCreating || editingId) ? (
          renderForm()
        ) : (
          <>
            <div className="calendar-manager__list">
              {allCalendars.map((calendar) => (
                <div key={calendar.id} className="calendar-manager__item">
                  <span
                    className="calendar-manager__item-color"
                    style={{ backgroundColor: calendar.color }}
                  />
                  <span className="calendar-manager__item-name">
                    {calendar.name}
                    {calendar.isDefault && (
                      <span className="calendar-manager__badge">{t('calendar.default')}</span>
                    )}
                  </span>
                  <div className="calendar-manager__item-actions">
                    <button
                      className="calendar-manager__item-btn"
                      onClick={() => handleStartEdit(calendar)}
                      title={t('common.edit')}
                    >
                      ✎
                    </button>
                    {!calendar.isDefault && (
                      <button
                        className="calendar-manager__item-btn calendar-manager__item-btn--danger"
                        onClick={() => handleDelete(calendar)}
                        title={t('common.delete')}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              className="calendar-manager__add"
              onClick={handleStartCreate}
            >
              + {t('calendar.createCalendar')}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

export default CalendarManagerModal;
